import { MessageType } from "../controllers/messaging";
import { ISystemConfig } from "../ts/Definitions";
import Database from "./database";

export const FEATURE_FLAG_GHOST_USER = "ghostUser";
export const FEATURE_FLAG_RESET_SESSION = "resetSession";
export const FEATURE_FLAG_HOUSE_MEMBER = "houseMember";
export const FEATURE_FLAG_FOUNDER = "founder";
export const FEATURE_FLAG_TRACE_LOGS = "traceLogs";

const FEATURE_FLAG_AUDIT_MESSAGE_TYPES = "auditMessageTypes";
const FEATURE_FLAG_RECEIPT_MESSAGE_TYPES = "sentReceiptMessageTypes";

const defaultConfig: ISystemConfig = {
  timeouts: {
    DATABASE_CONNECT_DELAY_MS: 1000,
    DATABASE_RECONNECT_MS: 2000,
    DATABASE_UPDATE_MS: 200,
    DATABASE_TRANSACTION_TIMEOUT: 1000,
  },
  allowLocks: true,
  bannedIps: [],
  featureFlags: {},
  help: {},
  hotTopics: {},
  ignoreDiscordBotCategories: [],
  perks: [],
  profanities: [],
  prohibitedSyntax: [],
  dashboardLayout: undefined,
  ownerProfileTypes: [],
  siteFxCuratedSources: {
    sources: [],
  },

};

export default class ConfigCache {
  configLoaded = false;
  private config = defaultConfig;
  private database: Database;

  constructor(database: Database) {
    this.database = database;
  }

  fetchConfig = async (): Promise<ISystemConfig> => {
    if (this.configLoaded && this.config && this.config !== defaultConfig) {
      return this.config;
    }

    // forcably clear the default config now we are fetching afresh
    this.config = undefined as unknown as ISystemConfig;

    const awaiter = new Promise<void>((resolve) => {
      this.database.loadConfig((updated: ISystemConfig | undefined) => {
        if (updated) {
          this.config = updated;
          this.listeners.forEach((cb) => cb(this.config));
        }
        resolve();
      });
    });

    await awaiter.then(() => {
      this.configLoaded = true;
    });

    return this.config;
  };

  private listeners: ((config: ISystemConfig) => void)[] = [];

  subscribe = (cb: (config: ISystemConfig) => void) => {
    this.listeners.push(cb);
    void this.fetchConfig();
    cb(this.config);
    return this.config;
  };
  unsubscribe = (cb: (config: ISystemConfig) => void) => {
    this.listeners.splice(this.listeners.indexOf(cb), 1);
  };

  auditedMessageType = (messageType: MessageType): boolean => {
    if (!this.config) return true;
    if (
      !this.config.featureFlags ||
      !this.config.featureFlags[FEATURE_FLAG_AUDIT_MESSAGE_TYPES]
    )
      return true;
    const matchingKey = Object.keys(
      this.config.featureFlags[FEATURE_FLAG_AUDIT_MESSAGE_TYPES]
    ).find((k) => k === MessageType[messageType]);
    if (matchingKey === undefined) return true;
    return (
      this.config.featureFlags[FEATURE_FLAG_AUDIT_MESSAGE_TYPES][
      matchingKey
      ] === true
    );
  };

  sentReceiptMessageType = (messageType: MessageType): boolean => {
    if (!this.config) return true;
    if (
      !this.config.featureFlags ||
      !this.config.featureFlags[FEATURE_FLAG_RECEIPT_MESSAGE_TYPES]
    )
      return false;
    const matchingKey = Object.keys(
      this.config.featureFlags[FEATURE_FLAG_RECEIPT_MESSAGE_TYPES]
    ).find((k) => k === MessageType[messageType]);
    if (matchingKey === undefined) return true;
    return (
      this.config.featureFlags[FEATURE_FLAG_RECEIPT_MESSAGE_TYPES][
      matchingKey
      ] === true
    );
  };

  allowedFeature = (
    flag: string,
    userName: string | undefined,
    defaultVal: boolean
  ): boolean => {
    if (!userName || !this.config) return defaultVal;
    if (!this.config.featureFlags || !this.config.featureFlags[flag])
      return defaultVal;
    const matchingKey = Object.keys(this.config.featureFlags[flag]).find(
      (k) => k?.toLowerCase() === userName?.toLowerCase()
    );
    if (!matchingKey) return defaultVal;
    return this.config.featureFlags[flag][matchingKey] !== false;
  };

  private hotTopics: Record<string, number> | undefined;

  // Lives outside /config so the config subscription no longer carries the counter map.
  getHotTopics = async (): Promise<Record<string, number>> => {
    if (this.hotTopics) return this.hotTopics;
    const stored = await this.database.getHotTopics();
    this.hotTopics =
      Object.keys(stored).length > 0 ? stored : { ...(this.config.hotTopics ?? {}) };
    return this.hotTopics;
  };

  incrementHelpTopicCount = (title: string): void => {
    if (!title) return;
    void this.getHotTopics().then((topics) => {
      topics[title] = (topics[title] ?? 0) + 1;
      void this.database.saveHotTopicsConfig(topics);
    });
  };
}
